import { AppError, TimeoutError, withTimeout } from '@fim/utils';

import type { ProviderOptions } from '../provider.js';
import type { AiProviderName } from '../types.js';

/**
 * Error thrown when an upstream AI provider returns a non-2xx response. Carries
 * the HTTP status and the (best-effort) parsed provider error payload so call
 * sites can branch on rate limits, auth failures, etc.
 */
export class AiProviderError extends AppError {
  readonly provider: AiProviderName;
  readonly status: number;
  readonly providerCode: string | undefined;

  constructor(args: {
    provider: AiProviderName;
    status: number;
    message: string;
    providerCode?: string;
    cause?: unknown;
  }) {
    super(args.message, {
      code: 'AI_PROVIDER_ERROR',
      statusCode: normalizeStatus(args.status),
      cause: args.cause,
    });
    this.name = 'AiProviderError';
    this.provider = args.provider;
    this.status = args.status;
    this.providerCode = args.providerCode;
  }

  /** Provider errors that are safe to retry (rate limit / transient server). */
  get retryable(): boolean {
    return this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

function normalizeStatus(status: number): number {
  return status >= 400 && status <= 599 ? status : 502;
}

export interface PostJsonArgs {
  readonly provider: AiProviderName;
  readonly url: string;
  readonly headers: Record<string, string>;
  readonly body: unknown;
  readonly timeoutMs: number;
  readonly fetchImpl: typeof fetch;
  readonly signal?: AbortSignal;
}

/**
 * POST a JSON body and return the raw `Response`. Applies a hard timeout and
 * converts non-2xx responses into a typed {@link AiProviderError}.
 *
 * The body is left unread so callers can either `.json()` it or consume the
 * SSE stream from `response.body`.
 */
export async function postJson(args: PostJsonArgs): Promise<Response> {
  const { provider, url, headers, body, timeoutMs, fetchImpl, signal } = args;

  // Pass a factory so `withTimeout` can abort the in-flight fetch on deadline.
  // The caller `signal` is merged into the timeout controller via options.
  const response = await withTimeout(
    (timeoutSignal) =>
      fetchImpl(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...headers },
        body: JSON.stringify(body),
        signal: timeoutSignal,
      }),
    timeoutMs,
    {
      ...(signal ? { signal } : {}),
      error: new TimeoutError(
        `${provider} request timed out after ${timeoutMs}ms`,
      ),
    },
  );

  if (!response.ok) {
    throw await toProviderError(provider, response);
  }

  return response;
}

/** Build an {@link AiProviderError} from a failed `Response`. */
async function toProviderError(
  provider: AiProviderName,
  response: Response,
): Promise<AiProviderError> {
  let message = `${provider} request failed with status ${response.status}`;
  let providerCode: string | undefined;

  try {
    const text = await response.text();
    if (text) {
      const parsed = JSON.parse(text) as {
        error?: { message?: string; type?: string; code?: string };
        message?: string;
      };
      const detail =
        parsed.error?.message ?? parsed.message ?? text.slice(0, 500);
      message = `${provider} request failed (${response.status}): ${detail}`;
      providerCode = parsed.error?.code ?? parsed.error?.type;
    }
  } catch {
    // Non-JSON or empty body — keep the generic message.
  }

  return new AiProviderError({
    provider,
    status: response.status,
    message,
    providerCode,
  });
}

/** Read and JSON-parse a successful response body with a typed cast. */
export async function readJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

/**
 * Resolve the effective timeout for a provider, preferring the explicit option
 * and falling back to a sane default.
 */
export function resolveTimeout(options: ProviderOptions | undefined): number {
  return options?.timeoutMs && options.timeoutMs > 0
    ? options.timeoutMs
    : 60_000;
}

/** Resolve the fetch implementation, preferring an injected one for testing. */
export function resolveFetch(options: ProviderOptions | undefined): typeof fetch {
  const impl = options?.fetch ?? globalThis.fetch;
  if (typeof impl !== 'function') {
    throw new AppError('No fetch implementation available in this runtime', {
      code: 'AI_NO_FETCH',
      statusCode: 500,
    });
  }
  return impl;
}
